import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Scale, Users, FileCheck2, BadgeCheck, Landmark } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines } from "../components/motion";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Testimonials } from "../components/Testimonials";
import { SITE, orgSchema, breadcrumbSchema, url } from "../data/site";

const trail = [["Home", "/"], ["About", "/about"]];

const stats = [
  ["50 + DC", "states where we run payroll and file returns"],
  ["$39/mo", "published entry price — no quote games"],
  ["$0", "per-state surcharge on every plan"],
  ["Same day", "written quote on every request"],
];

const beliefs = [
  [Scale, "Published prices", "Payroll pricing should never require a sales call. Every plan, base fee and per-person rate is on our pricing page, next to Gusto's published prices for comparison."],
  [ShieldCheck, "A professional checks the work", "Software calculates; a licensed CPA reviews. On Plus and Complete, every payroll is checked before it runs — classification, withholding, and state registrations included."],
  [Users, "One team, not three vendors", "Payroll, bookkeeping and business tax answered by the same team, so year-end W-2s, your books and your tax return always agree."],
];

const credentials = [
  [BadgeCheck, "CPA-led", "ABSLpay is run by an experienced Certified Public Accountant who also holds the Chartered Accountant credential, serving business clients across the country."],
  [Landmark, "Built inside a tax practice", "We grew out of an established tax practice — payroll here is handled by people who also file the returns it feeds into."],
  [FileCheck2, "Compliance monitored year-round", "State rate notices, wage-base changes and new paid-leave programs are tracked for every state where our clients pay employees."],
];

export default function About() {
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: `About ${SITE.brand}`,
    url: url("/about"),
    mainEntity: { "@id": url("/#org") },
  };

  return (
    <>
      <Seo
        title="About ABSLpay | CPA-Led Payroll Services for All 50 States"
        description="ABSLpay is a CPA-led payroll firm built inside a tax practice. Published pricing, CPA-reviewed payroll runs and compliance in all 50 states and Washington, DC."
        path="/about"
        jsonLd={[orgSchema(), aboutSchema, breadcrumbSchema(trail)]}
      />
      <Breadcrumbs trail={trail} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,#ECFDF5_0%,transparent_50%)]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 lg:pt-16 lg:pb-20">
          <MaskedLines
            lines={["The payroll firm", "built inside", "a tax practice."]}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900"
          />
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-600">
            ABSLpay — a service of {SITE.legalName}, based in {SITE.city}, {SITE.region} — exists because our tax
            clients kept bringing us payroll problems their payroll software created. So we built the payroll service we
            kept wishing they had: published prices, every state covered, and a CPA looking at the numbers before anyone
            gets paid.
          </p>
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map(([big, small], i) => (
            <Reveal key={big} delay={i * 0.07}>
              <div data-testid={`about-stat-${i}`}>
                <span className="font-mono-num text-3xl sm:text-4xl font-bold text-emerald-700">{big}</span>
                <p className="mt-2 text-sm leading-snug text-slate-600">{small}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-700">What we believe</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              Payroll is a professional service, not just software
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {beliefs.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 transition-colors hover:border-emerald-300">
                  <Icon size={22} className="text-emerald-600" />
                  <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-slate-950 grain">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-400">Credentials</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              Why businesses trust us with payroll
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {credentials.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-800 bg-slate-900/60 p-7 backdrop-blur-sm transition-colors hover:border-emerald-700">
                  <Icon size={22} className="text-emerald-400" />
                  <h3 className="mt-4 font-display text-lg font-bold text-white">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Testimonials eyebrow="Proof over promises" />

      <section>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              Talk to the team that will actually run your payroll
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-600">
              No sales handoff — the person who quotes you is on the team that files your returns.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-testid="about-get-quote-btn" className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white font-bold px-7 py-3.5 hover:bg-emerald-700 transition-colors">
                Get a free quote <ArrowRight size={17} />
              </Link>
              <a href={`tel:${SITE.phone}`} data-testid="about-call-btn" className="inline-flex items-center rounded-full border border-slate-300 bg-white font-bold px-7 py-3.5 text-slate-800 hover:border-slate-900 transition-colors">
                Call {SITE.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
